import { useEffect, type RefObject } from 'react'

import { useReducedMotion } from '../../ui/motion'

interface ScrollOptions {
  /** The value an effect is given when it does not track the scroll. */
  settled?: number
  /**
   * Whether to keep tracking under reduced motion. A decorative effect (a
   * word lighting up, a line of type sliding) stops and shows its finished
   * state; the story stage keeps following the reader, because there the
   * scroll position is the content, and only the animation between steps is
   * switched off (by the engine).
   */
  evenWhenReduced?: boolean
}

/**
 * How far an element has travelled through the viewport, as 0…1.
 *
 * `measure` turns the element's rectangle and the viewport height into the
 * progress a particular effect wants (the story wants "through the section",
 * a reveal wants "past the reading line"). `apply` receives it at most once
 * per animation frame while the page scrolls; it writes a CSS custom property
 * or hands the number to a canvas, never React state, so scrolling renders
 * nothing.
 *
 * Where the environment cannot answer media queries at all (jsdom), the
 * effect is given its settled value once and nothing is installed.
 */
export function useScrollProgress<T extends HTMLElement>(
  ref: RefObject<T | null>,
  measure: (rect: DOMRect, viewport: number) => number,
  apply: (progress: number, element: T) => void,
  { settled = 1, evenWhenReduced = false }: ScrollOptions = {},
) {
  const reduced = useReducedMotion()
  useEffect(() => {
    const element = ref.current
    if (!element) return undefined
    if (typeof window.matchMedia !== 'function' || (reduced && !evenWhenReduced)) {
      apply(settled, element)
      return undefined
    }
    let frame = 0
    const update = () => {
      frame = 0
      const progress = measure(element.getBoundingClientRect(), window.innerHeight || 1)
      apply(Math.min(1, Math.max(0, progress)), element)
    }
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [ref, measure, apply, settled, evenWhenReduced, reduced])
}

/** Through a tall section: 0 when its top meets the top of the screen, 1 when its bottom meets the bottom. */
export function throughSection(rect: DOMRect, viewport: number): number {
  return -rect.top / Math.max(1, rect.height - viewport)
}

/** Across the screen: 0 as the element's top enters at the bottom, 1 as its bottom leaves at the top. */
export function acrossScreen(rect: DOMRect, viewport: number): number {
  return (viewport - rect.top) / (viewport + rect.height)
}

/** Into view: 0 as the element's top enters at the bottom, 1 once its top is a quarter of the way down. */
export function intoView(rect: DOMRect, viewport: number): number {
  return (viewport - rect.top) / Math.max(1, viewport * 0.75)
}

/** Past the reading line: 0 with the element's top at 85% of the screen, 1 once its bottom passes 40%. */
export function pastReadingLine(rect: DOMRect, viewport: number): number {
  return (viewport * 0.85 - rect.top) / Math.max(1, rect.height + viewport * 0.45)
}

/** Writes the progress to `--progress` on the element. */
export function writeProgress(progress: number, element: HTMLElement) {
  element.style.setProperty('--progress', progress.toFixed(4))
}
